import type { ValidationSchemaApi } from '../../models';
import {
  ADDRESS_BOOK_LABELS,
  ROUTING_GUIDE_LABELS,
  SPOT_QUOTE_LABELS,
  SPOT_QUOTE_RATE_LABELS,
  VALIDATION_SCHEMA_KEYS,
  type ValidationSchema,
  type ValidationSchemaKey,
  type ValidationSchemas,
} from '../../models';
import type { JArray, JObject, JProperties, JSchema } from '../../types';
import { parseJSONSchema, resolvePropertiesLabels } from '../../utils';

export const mapPropertyLabels = (schemaKey: ValidationSchemaKey) => {
  const mapper = {
    [VALIDATION_SCHEMA_KEYS.addressBook.record]: ADDRESS_BOOK_LABELS,
    [VALIDATION_SCHEMA_KEYS.routingGuide.create]: ROUTING_GUIDE_LABELS,
    [VALIDATION_SCHEMA_KEYS.spotQuote.read]: SPOT_QUOTE_LABELS,
    [VALIDATION_SCHEMA_KEYS.spotQuote.create]: SPOT_QUOTE_LABELS,
    [VALIDATION_SCHEMA_KEYS.spotQuote.edit]: SPOT_QUOTE_LABELS,
    [VALIDATION_SCHEMA_KEYS.spotQuoteRate.read('any')]: SPOT_QUOTE_RATE_LABELS,
  } as const;

  return mapper[schemaKey] || {};
};

/**
 * normalizePortsSchema
 *
 * Temporary fix for handling `ports` only.
 * Once the new form structure is in place,
 * this should be refactored or removed.
 */
export const normalizePortsSchema = (schema: JSchema): JSchema => {
  if (!('properties' in schema) || !schema.properties) {
    return schema;
  }

  const { properties } = schema;

  if (!properties.ports) {
    return schema;
  }

  const portsSchema = properties.ports as JArray;
  if (!('definitions' in schema) || !schema.definitions) {
    return schema;
  }

  // --- base port definition
  const portDef = ((): JObject | undefined => {
    if ('items' in portsSchema && portsSchema.items) {
      if ('$ref' in portsSchema.items) {
        const refName = portsSchema.items.$ref.replace('#/definitions/', '');
        return schema.definitions?.[refName] as JObject;
      }
      if ('properties' in portsSchema.items) {
        return portsSchema.items as JObject;
      }
    }
    return undefined;
  })();

  if (!portDef) {
    return schema;
  }

  const { properties: portProps = {}, required: portReq = [] } = portDef;

  // shipper
  const shipperDef = schema.definitions.QuoteShipperModelSchema as JObject;
  const { properties: shipperProps = {}, required: shipperReq = [] } = shipperDef;

  // consignee
  const consigneeDef = schema.definitions.QuoteConsigneeModelSchema as JObject;
  const { properties: consigneeProps = {}, required: consigneeReq = [] } = consigneeDef;

  // shipping record
  const recordDef = schema.definitions.QuoteShippingRecordModelSchema as JObject;
  const { properties: recordProps = {}, required: recordReq = [] } = recordDef;

  // flatten properties
  const mergedProps: JProperties = {
    ...portProps,
    ...shipperProps,
    ...consigneeProps,
    ...recordProps,
  };

  // merge required fields, force sequence required for consignee
  const mergedRequired = Array.from(new Set([...portReq, ...shipperReq, ...consigneeReq, ...recordReq, 'sequence']));

  // create new ports.items
  const newPortsItems: JObject = {
    properties: mergedProps,
    type: 'object',
    ...(mergedRequired.length > 0 ? { required: mergedRequired } : {}),
  };

  // apply labels
  const labeledPortsItems = resolvePropertiesLabels(
    newPortsItems,
    mapPropertyLabels('CreateQuote'),
    schema.definitions,
  );

  properties.ports = {
    items: labeledPortsItems,
    minItems: portsSchema.minItems,
    type: portsSchema.type,
  } satisfies JArray;

  return {
    ...schema,
    properties,
  };
};

export const parseValidationSchema = (data: string, schemaKey: ValidationSchemaKey): JSchema => {
  const schema = parseJSONSchema(data);
  const schemaLabels = mapPropertyLabels(schemaKey);

  const schemaWithNormalizedPorts = normalizePortsSchema(schema);

  return resolvePropertiesLabels(schemaWithNormalizedPorts, schemaLabels);
};

export const normalizeValidationSchemas = (data: ValidationSchemaApi[]): Partial<ValidationSchemas> => {
  if (!data || data.length === 0) {
    return {};
  }
  return data.reduce((acc, curr) => {
    if (curr.schemaKey && curr.validationSchema) {
      const rawLookupCodeId = curr.lookupCodeId || '';
      const lookupCodeId = rawLookupCodeId.trim();
      const schemaKey = (
        curr.lookupCodeId ? `${curr.schemaKey}:${lookupCodeId}` : curr.schemaKey
      ) as ValidationSchemaKey;

      const schema: ValidationSchema = {
        ...curr,
        schemaKey,
        validationSchema: parseValidationSchema(
          curr.validationSchema,
          (curr.lookupCodeId ? `${curr.schemaKey}:any` : curr.schemaKey) as ValidationSchemaKey,
        ),
      } as ValidationSchema;
      acc[schemaKey] = schema;
    }
    return acc;
  }, {} as ValidationSchemas);
};
